import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Bell, Check, Trash2, TrendingUp, TrendingDown, Trophy, Target, AlertTriangle,
  Plus, Settings, X, Power, Pencil, Mail, MailX, Info, Sparkles,
} from "lucide-react";

const TYPE_META = {
  major_drop: { label: "Major drop", icon: TrendingDown, tone: "danger" },
  major_gain: { label: "Major gain", icon: TrendingUp, tone: "success" },
  entered_top10: { label: "Entered top 10", icon: Target, tone: "success" },
  entered_top3: { label: "Entered top 3", icon: Trophy, tone: "success" },
  competitor_overtake: { label: "Competitor overtake", icon: AlertTriangle, tone: "warning" },
};

const TRIGGER_META = [
  { key: "major_gain", label: "Improved by ≥ N positions", withThreshold: true, icon: TrendingUp },
  { key: "major_drop", label: "Dropped by ≥ N positions", withThreshold: true, icon: TrendingDown },
  { key: "entered_top10", label: "Entered Top 10", withThreshold: false, icon: Target },
  { key: "entered_top3", label: "Entered Top 3", withThreshold: false, icon: Trophy },
  // TODO(competitor-overtake): restore when the competitor tracking UI is re-enabled.
  // { key: "competitor_overtake", label: "Competitor overtake", withThreshold: false, icon: AlertTriangle },
];

const BLANK_RULE = {
  name: "New alert rule",
  scope: { kind: "all", project_id: null, tag: null, keyword_ids: [] },
  triggers: {
    major_gain: { enabled: true, threshold: 5 },
    major_drop: { enabled: true, threshold: 5 },
    entered_top10: { enabled: true },
    entered_top3: { enabled: true },
    // TODO(competitor-overtake): restore when the competitor tracking UI is re-enabled.
    competitor_overtake: { enabled: false },
  },
  email_enabled: true,
  active: true,
};


export default function AlertsPage() {
  const [tab, setTab] = useState("feed"); // feed | rules
  return (
    <div className="p-8 lg:p-10">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">Alerts</div>
          <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">Only the signal.</h1>
        </div>
        <div className="inline-flex rounded-md border border-[#E2E8F0] p-1 bg-white">
          <button
            onClick={() => setTab("feed")}
            data-testid="tab-feed"
            className={`px-3 py-1.5 rounded text-sm inline-flex items-center gap-1.5 transition ${tab === 'feed' ? 'bg-[#0047FF] text-white' : 'text-[#525B6A] hover:text-[#0A0D14]'}`}
          >
            <Bell className="h-3.5 w-3.5" /> Feed
          </button>
          <button
            onClick={() => setTab("rules")}
            data-testid="tab-rules"
            className={`px-3 py-1.5 rounded text-sm inline-flex items-center gap-1.5 transition ${tab === 'rules' ? 'bg-[#0047FF] text-white' : 'text-[#525B6A] hover:text-[#0A0D14]'}`}
          >
            <Settings className="h-3.5 w-3.5" /> Rules
          </button>
        </div>
      </header>

      {tab === "feed" ? <FeedTab /> : <RulesTab />}
    </div>
  );
}


// ---------- FEED ----------
function FeedTab() {
  const [alerts, setAlerts] = useState([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const load = () => api.get("/alerts", { params: { unread_only: unreadOnly } }).then((r) => setAlerts(r.data));
  useEffect(() => { load(); }, [unreadOnly]);

  const markRead = async (id) => { await api.post(`/alerts/${id}/read`); load(); };
  const markAll = async () => { await api.post("/alerts/read-all"); toast.success("All alerts marked read."); load(); };
  const del = async (id) => { await api.delete(`/alerts/${id}`); load(); };

  return (
    <>
      <div className="mt-6 flex items-center gap-2">
        <button onClick={() => setUnreadOnly(!unreadOnly)} data-testid="alerts-filter-unread" className={`pill ${unreadOnly ? 'pill-accent' : ''}`}>
          {unreadOnly ? "Unread only" : "All alerts"}
        </button>
        <button onClick={markAll} data-testid="alerts-mark-all" className="btn-ghost px-3 py-1.5 rounded-md text-sm inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Mark all read</button>
      </div>
      <div className="mt-6 space-y-2">
        {alerts.length === 0 && (
          <div className="card-crisp p-10 text-center">
            <Bell className="h-8 w-8 mx-auto mb-3 text-[#E2E8F0]" />
            <div className="text-[#525B6A]">No alerts. That&apos;s a good thing.</div>
          </div>
        )}
        {alerts.map((a) => {
          const meta = TYPE_META[a.type] || { label: a.type, icon: Bell, tone: "" };
          const Icon = meta.icon;
          return (
            <div key={a.id} data-testid={`alert-${a.id}`} className={`card-crisp p-4 flex items-start gap-3 ${a.read ? 'opacity-60' : ''}`}>
              <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${meta.tone === 'success' ? 'bg-emerald-50 text-emerald-700' : meta.tone === 'danger' ? 'bg-red-50 text-red-700' : meta.tone === 'warning' ? 'bg-amber-50 text-amber-700' : 'bg-[#E0E7FF] text-[#0047FF]'}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs text-[#8A94A6]">
                  <span className="uppercase tracking-wider font-medium">{meta.label}</span>
                  <span>·</span>
                  <span>{new Date(a.created_at).toLocaleString()}</span>
                  {!a.read && <span className="pill pill-accent" style={{ padding: '1px 6px', fontSize: 10 }}>New</span>}
                </div>
                <div className="mt-1 text-[#0A0D14]">{a.payload?.summary}</div>
                {a.payload?.keyword_id && (
                  <Link to={`/app/projects/${a.project_id}`} className="mt-1 text-xs text-[#0047FF] inline-block">Open project →</Link>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!a.read && <button onClick={() => markRead(a.id)} data-testid={`alert-read-${a.id}`} className="p-1.5 text-[#525B6A] hover:text-[#0047FF]" title="Mark read"><Check className="h-3.5 w-3.5" /></button>}
                <button onClick={() => del(a.id)} data-testid={`alert-del-${a.id}`} className="p-1.5 text-[#525B6A] hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}


// ---------- RULES ----------
function RulesTab() {
  const [rules, setRules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // rule object or null

  const load = () => api.get("/alert-rules").then((r) => setRules(r.data));
  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data || []));
  }, []);

  const startNew = () => setEditing(JSON.parse(JSON.stringify(BLANK_RULE)));
  const startEdit = (rule) => setEditing(JSON.parse(JSON.stringify(rule)));

  const toggleRule = async (id) => { await api.post(`/alert-rules/${id}/toggle`); load(); };
  const delRule = async (id) => {
    if (!window.confirm("Delete this alert rule?")) return;
    await api.delete(`/alert-rules/${id}`);
    toast.success("Rule deleted.");
    load();
  };

  return (
    <>
      <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
        <p className="text-sm text-[#525B6A] max-w-2xl leading-relaxed">
          Alert rules decide <strong>which keywords</strong> get watched and <strong>which ranking events</strong> fire an alert. Each rule can target all keywords, a single project, a tag, or a specific list.
          A default rule is seeded for you — edit it, add more, or turn any of them off.
        </p>
        <button onClick={startNew} data-testid="new-rule-btn" className="btn-primary px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-2 shrink-0">
          <Plus className="h-4 w-4" /> New rule
        </button>
      </div>

      <div className="mt-6 space-y-2">
        {rules.length === 0 && (
          <div className="card-crisp p-10 text-center">
            <Sparkles className="h-8 w-8 mx-auto mb-3 text-[#E2E8F0]" />
            <div className="text-[#525B6A]">No rules yet. <button onClick={startNew} className="text-[#0047FF] font-medium">Create your first →</button></div>
          </div>
        )}
        {rules.map((rule) => (
          <RuleRow key={rule.id} rule={rule} projects={projects} onEdit={() => startEdit(rule)} onToggle={() => toggleRule(rule.id)} onDelete={() => delRule(rule.id)} />
        ))}
      </div>

      {editing && (
        <RuleEditor
          initial={editing}
          projects={projects}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}


function RuleRow({ rule, projects, onEdit, onToggle, onDelete }) {
  const activeTriggers = TRIGGER_META.filter((t) => rule.triggers?.[t.key]?.enabled);
  const proj = projects.find((p) => p.id === rule.scope?.project_id);
  const projName = proj?.name || proj?.domain || rule.scope?.project_id?.slice(0, 6) || "—";
  const kwCount = (rule.scope?.keyword_ids || []).length;
  const scopeLabel =
    rule.scope?.kind === "all" ? "All keywords across all projects"
    : rule.scope?.kind === "project"
      ? (kwCount > 0
          ? `${projName} · ${kwCount} specific keyword${kwCount === 1 ? '' : 's'}`
          : `${projName} · entire project`)
    : rule.scope?.kind === "tag" ? `Tag · ${rule.scope.tag}`
    : "—";
  return (
    <div className={`card-crisp p-5 ${rule.active ? '' : 'opacity-60'}`} data-testid={`rule-row-${rule.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-base font-display font-semibold text-[#0A0D14]">{rule.name}</div>
            {rule.is_default && <span className="pill pill-accent" style={{ padding: '1px 6px', fontSize: 10 }}>Default</span>}
            {!rule.active && <span className="pill" style={{ padding: '1px 6px', fontSize: 10 }}>Paused</span>}
            {rule.email_enabled ? (
              <span className="text-[10px] text-[#525B6A] uppercase tracking-wider font-medium inline-flex items-center gap-1"><Mail className="h-3 w-3" /> Email on</span>
            ) : (
              <span className="text-[10px] text-[#8A94A6] uppercase tracking-wider font-medium inline-flex items-center gap-1"><MailX className="h-3 w-3" /> Email off</span>
            )}
          </div>
          <div className="mt-1 text-xs text-[#525B6A]">{scopeLabel}</div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {activeTriggers.map((t) => {
              const T = t.icon;
              const thresh = t.withThreshold ? ` ≥${rule.triggers?.[t.key]?.threshold}` : "";
              return (
                <span key={t.key} className="pill" style={{ padding: '2px 8px', fontSize: 10 }}>
                  <T className="h-3 w-3" /> {t.label.replace(" ≥ N positions", "")}{thresh}
                </span>
              );
            })}
            {activeTriggers.length === 0 && <span className="text-[11px] text-[#8A94A6] italic">No triggers enabled</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onToggle} data-testid={`rule-toggle-${rule.id}`} className="p-1.5 text-[#525B6A] hover:text-[#0047FF]" title={rule.active ? "Pause rule" : "Resume rule"}>
            <Power className="h-3.5 w-3.5" />
          </button>
          <button onClick={onEdit} data-testid={`rule-edit-${rule.id}`} className="p-1.5 text-[#525B6A] hover:text-[#0047FF]" title="Edit rule">
            <Pencil className="h-3.5 w-3.5" />
          </button>
          {!rule.is_default && (
            <button onClick={onDelete} data-testid={`rule-delete-${rule.id}`} className="p-1.5 text-[#525B6A] hover:text-red-600" title="Delete rule">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function RuleEditor({ initial, projects, onClose, onSaved }) {
  const [rule, setRule] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [projectKeywords, setProjectKeywords] = useState([]); // for keyword sub-picker
  const [kwFilter, setKwFilter] = useState("");
  const isNew = !initial.id;

  const setScope = (patch) => setRule({ ...rule, scope: { ...rule.scope, ...patch } });
  const setTrigger = (key, patch) => setRule({ ...rule, triggers: { ...rule.triggers, [key]: { ...rule.triggers[key], ...patch } } });

  // Load the chosen project's keywords when scope=project + project_id is set
  useEffect(() => {
    if (rule.scope?.kind !== "project" || !rule.scope?.project_id) {
      setProjectKeywords([]);
      return;
    }
    api.get("/keywords", { params: { project_id: rule.scope.project_id, limit: 2000 } })
      .then((r) => setProjectKeywords(r.data || []))
      .catch(() => setProjectKeywords([]));
  }, [rule.scope?.kind, rule.scope?.project_id]);

  const toggleKw = (id) => {
    const cur = new Set(rule.scope.keyword_ids || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    setScope({ keyword_ids: Array.from(cur) });
  };

  const save = async () => {
    if (!rule.name?.trim()) return toast.error("Give the rule a name.");
    if (rule.scope?.kind === "project" && !rule.scope?.project_id) return toast.error("Pick a project for this rule.");
    if (rule.scope?.kind === "tag" && !rule.scope?.tag?.trim()) return toast.error("Enter a tag for this rule.");
    setBusy(true);
    try {
      if (isNew) await api.post("/alert-rules", rule);
      else await api.put(`/alert-rules/${rule.id}`, rule);
      toast.success(isNew ? "Rule created." : "Rule updated.");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  const runPreview = async () => {
    if (isNew) return; // preview only for saved rules
    try {
      const r = await api.get(`/alert-rules/${rule.id}/preview`);
      setPreview(r.data.count);
    } catch (e) { console.debug("[AlertsPage] rule preview failed:", e); }
  };
  useEffect(() => { if (!isNew) runPreview(); }, []);

  const filteredKws = projectKeywords.filter((k) => !kwFilter || (k.keyword || "").toLowerCase().includes(kwFilter.toLowerCase()));
  const selectedIds = new Set(rule.scope?.keyword_ids || []);
  const watchAllInProject = selectedIds.size === 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="rule-editor">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg max-w-xl w-full shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">{isNew ? "New alert rule" : "Edit alert rule"}</div>
            <div className="text-lg font-display font-semibold text-[#0A0D14]">{rule.name || "Untitled rule"}</div>
          </div>
          <button onClick={onClose} className="text-[#8A94A6] hover:text-[#0A0D14]"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label className="text-xs font-semibold text-[#0A0D14] mb-1 block">Rule name</label>
            <input data-testid="rule-name" value={rule.name} onChange={(e) => setRule({ ...rule, name: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" placeholder="e.g. Priority keywords drop watch" />
          </div>

          {/* Scope */}
          <div>
            <div className="text-xs font-semibold text-[#0A0D14] mb-2">Watch which keywords?</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["all", "All my keywords"],
                ["project", "Specific project"],
                // TODO(scope-by-tag): restore when a client-tag / keyword-tag
                // UI is reintroduced. Backend evaluator + scopeLabel already
                // handle the "tag" case so re-enabling is one line here.
                // ["tag", "By tag"],
              ].map(([kind, label]) => (
                <button key={kind} onClick={() => setScope({ kind, keyword_ids: [] })} data-testid={`scope-${kind}`}
                  className={`text-left p-3 rounded-md border text-xs transition ${rule.scope?.kind === kind ? 'border-[#0047FF] bg-[#0047FF]/[0.04] text-[#0047FF] font-semibold' : 'border-[#E2E8F0] text-[#525B6A] hover:border-[#8A94A6]'}`}
                >{label}</button>
              ))}
            </div>
            {rule.scope?.kind === "project" && (
              <>
                <select data-testid="scope-project-picker" value={rule.scope.project_id || ""} onChange={(e) => setScope({ project_id: e.target.value, keyword_ids: [] })} className="mt-3 w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                  <option value="">— Select project —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.domain}</option>)}
                </select>
                {rule.scope.project_id && (
                  <div className="mt-3 border border-[#E2E8F0] rounded-md">
                    <div className="px-3 py-2 border-b border-[#E2E8F0] flex items-center justify-between gap-2 bg-[#F8F9FA]">
                      <div className="text-[11px] font-semibold text-[#0A0D14]">
                        Keywords to watch
                        <span className="ml-1.5 text-[10px] uppercase tracking-wider font-medium text-[#8A94A6]">
                          {watchAllInProject ? "· entire project" : `· ${selectedIds.size} selected`}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setScope({ keyword_ids: [] })}
                          data-testid="scope-kw-clear"
                          className="text-[10px] px-2 py-0.5 rounded border border-[#E2E8F0] text-[#525B6A] hover:border-[#0047FF] hover:text-[#0047FF]"
                        >
                          Clear all
                        </button>
                        <button
                          onClick={() => setScope({ keyword_ids: filteredKws.map((k) => k.id) })}
                          data-testid="scope-kw-selectall"
                          className="text-[10px] px-2 py-0.5 rounded border border-[#E2E8F0] text-[#525B6A] hover:border-[#0047FF] hover:text-[#0047FF]"
                        >
                          Select all
                        </button>
                      </div>
                    </div>
                    <div className="px-3 py-2 border-b border-[#E2E8F0]">
                      <input
                        value={kwFilter}
                        onChange={(e) => setKwFilter(e.target.value)}
                        placeholder="Search keywords in this project…"
                        data-testid="scope-kw-search"
                        className="w-full px-2 py-1 text-xs border border-[#E2E8F0] rounded"
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto p-1" data-testid="scope-kw-list">
                      {filteredKws.length === 0 && (
                        <div className="text-[11px] text-[#8A94A6] italic px-3 py-6 text-center">
                          {projectKeywords.length === 0 ? "This project has no keywords yet." : "No keywords match that search."}
                        </div>
                      )}
                      {filteredKws.slice(0, 300).map((k) => {
                        const on = selectedIds.has(k.id);
                        return (
                          <label
                            key={k.id}
                            data-testid={`scope-kw-${k.id}`}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer ${on ? 'bg-[#0047FF]/[0.06]' : 'hover:bg-[#F8F9FA]'}`}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleKw(k.id)}
                              className="h-3.5 w-3.5 accent-[#0047FF] shrink-0"
                            />
                            <span className={`text-xs truncate ${on ? 'text-[#0A0D14] font-medium' : 'text-[#525B6A]'}`}>{k.keyword}</span>
                            <span className="ml-auto text-[10px] font-mono text-[#8A94A6] shrink-0">
                              {k.current_rank ? `#${k.current_rank}` : '—'} · {k.country}
                            </span>
                          </label>
                        );
                      })}
                      {filteredKws.length > 300 && (
                        <div className="text-[10px] text-[#8A94A6] italic px-3 py-2 text-center">
                          {filteredKws.length - 300} more not shown — narrow the search.
                        </div>
                      )}
                    </div>
                    <div className="px-3 py-2 border-t border-[#E2E8F0] bg-[#F8F9FA] text-[10px] text-[#525B6A] leading-relaxed">
                      <Info className="inline h-3 w-3 mr-1 text-[#8A94A6]" />
                      Leave nothing selected to watch every keyword in the project. Pick specific ones to narrow scope.
                    </div>
                  </div>
                )}
              </>
            )}
            {rule.scope?.kind === "tag" && (
              // TODO(scope-by-tag): restore the picker button above to re-enable
              // creating tag-scoped rules. Kept here for backward-compat on any
              // legacy rules already saved with this scope.
              <input data-testid="scope-tag" value={rule.scope.tag || ""} onChange={(e) => setScope({ tag: e.target.value })} placeholder="e.g. priority" className="mt-3 w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
            )}
            {preview !== null && !isNew && (
              <div className="mt-2 text-[11px] text-[#525B6A]">
                Currently matches <strong className="text-[#0A0D14]">{preview}</strong> keyword{preview === 1 ? '' : 's'}.
              </div>
            )}
          </div>

          {/* Triggers */}
          <div>
            <div className="text-xs font-semibold text-[#0A0D14] mb-2">Fire an alert when…</div>
            <div className="space-y-2">
              {TRIGGER_META.map((t) => {
                const T = t.icon;
                const cfg = rule.triggers?.[t.key] || { enabled: false };
                return (
                  <label key={t.key} data-testid={`trigger-${t.key}`}
                    className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer ${cfg.enabled ? 'border-[#0047FF] bg-[#0047FF]/[0.04]' : 'border-[#E2E8F0] hover:border-[#8A94A6]'}`}
                  >
                    <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setTrigger(t.key, { enabled: e.target.checked })} data-testid={`trigger-${t.key}-toggle`} className="h-4 w-4 shrink-0 accent-[#0047FF]" />
                    <T className={`h-4 w-4 shrink-0 ${cfg.enabled ? 'text-[#0047FF]' : 'text-[#8A94A6]'}`} />
                    <div className="flex-1 text-sm text-[#0A0D14]">{t.label.replace(" ≥ N positions", t.withThreshold ? " ≥ " : "")}
                      {t.withThreshold && (
                        <input
                          type="number" min="1" max="99"
                          value={cfg.threshold ?? 5}
                          onChange={(e) => setTrigger(t.key, { threshold: parseInt(e.target.value, 10) || 1 })}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`trigger-${t.key}-threshold`}
                          disabled={!cfg.enabled}
                          className="ml-1 w-14 px-1.5 py-0.5 border border-[#E2E8F0] rounded text-sm tabular-nums disabled:opacity-40"
                        />
                      )}
                      {t.withThreshold && <span className="ml-1 text-xs text-[#525B6A]">positions</span>}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Delivery */}
          <div>
            <div className="text-xs font-semibold text-[#0A0D14] mb-2">Delivery</div>
            <label className="flex items-center gap-3 p-3 rounded-md border border-[#E2E8F0] cursor-pointer">
              <input type="checkbox" checked={rule.email_enabled} onChange={(e) => setRule({ ...rule, email_enabled: e.target.checked })} data-testid="rule-email" className="h-4 w-4 accent-[#0047FF]" />
              <Mail className="h-4 w-4 text-[#525B6A]" />
              <div className="flex-1 text-sm text-[#0A0D14]">Also send an email <span className="text-xs text-[#8A94A6]">(in-app bell is always on)</span></div>
            </label>
            <label className="mt-2 flex items-center gap-3 p-3 rounded-md border border-[#E2E8F0] cursor-pointer">
              <input type="checkbox" checked={rule.active} onChange={(e) => setRule({ ...rule, active: e.target.checked })} data-testid="rule-active" className="h-4 w-4 accent-[#0047FF]" />
              <Power className="h-4 w-4 text-[#525B6A]" />
              <div className="flex-1 text-sm text-[#0A0D14]">Rule active <span className="text-xs text-[#8A94A6]">— uncheck to pause without deleting</span></div>
            </label>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-2 sticky bottom-0 bg-[#F8F9FA]">
          <button onClick={onClose} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="rule-save" className="btn-primary px-4 py-2 rounded-md text-sm disabled:opacity-50">{busy ? "Saving…" : isNew ? "Create rule" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}
